import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useTranslation} from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import {IconButton, ListItemIcon, ListItemText} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import Popover from '@mui/material/Popover';
import {isEmpty} from 'lodash';
import moment from 'moment';
import {getConfig} from '../../../util/configUtil';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import {useTheme} from '@mui/material/styles';
import {setNoteFilters} from '../../../store/reducers/notes';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

const useStyles = makeStyles((theme) => ({
    container: {
        width: 300,
        backgroundColor: theme.palette.mode === 'light' ? '#eff1f3' : '#252525',
        border: `solid 1px ${theme.palette.admissionForm.borderColor}`,
        borderRadius: 6,
        padding: '5px 10px',
        cursor: 'pointer',
    },
    placeholder: {
        fontSize: 'small',
        color: theme.palette.admissionForm.labelColor,
        userSelect: 'none',
    },
    selected: {
        fontSize: 'small',
        userSelect: 'none',
        fontWeight: 'bold',
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconButton: {
        backgroundColor: theme.palette.inPatient.iconBackground,
        color: theme.palette.mode === 'dark' ? '#222222' : '#ffffff',
        borderRadius: 4,
        padding: 2,
        '&.clear': {
            backgroundColor: 'transparent',
            color: theme.palette.mode !== 'dark' ? '#222222' : '#ffffff',
        },
    },
    attendanceType: {
        width: '16px !important',
        height: '16px !important',
        borderRadius: '50%',
    },
}));

const DEFAULT_SELECTED_VALUE = {
    index: null,
    admission: null,
};

const ActivitySpellSelector = ({type, setCurrentActivity, admissions = []}) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const {t} = useTranslation();
    const theme = useTheme();

    // Config
    const dateFormat = getConfig('defaultDateTimeFormat');

    // Admission states
    const [selected, setSelected] = useState(DEFAULT_SELECTED_VALUE);

    // Menu
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? 'admissions-popover' : undefined;

    const filteredAdmission = useCallback(admissions.filter(adm => adm !== null), [admissions]);

    useEffect(() => {
        switch (type) {
            case 'note':
                dispatch(setNoteFilters({
                    activitySelectedNotes: selected.admission || null,
                    expanded: !!selected?.admission,
                }));
                break;
            case 'activity':
                if (setCurrentActivity) setCurrentActivity(selected.admission || null);
                break;
            default:
                console.error(`Wrong type`);
        }
    }, [selected.admission]);

    const handleClick = (event) => {
        if (event.target.closest('.disabled-options') === null) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSelectNext = () => {
        if (!isEmpty(admissions)) {
            const currentIndex = selected.index === null ? -1 : selected.index;
            setSelected({
                index: currentIndex + 1,
                admission: admissions[currentIndex + 1],
            });
        }
    };

    const handleSelectPrev = () => {
        if (!isEmpty(admissions) && selected.index - 1 >= 0) {
            setSelected({
                index: selected.index - 1,
                admission: admissions[selected.index - 1],
            });
        }
    };

    const handleClear = () => {
        setSelected(DEFAULT_SELECTED_VALUE);
    };

    const selectAdmission = (admission, index) => {
        setSelected({
            admission,
            index,
        });
        handleCloseMenu();
    };

    const getAdmissionType = (admission) => {
        switch (admission?.attendanceType) {
            case 'E' :
                return t('ED');
            case 'I' :
                return t('Inpatient');
            case 'O' :
                return t('Outpatient');
            default:
                return 'N/N';
        }
    };

    const renderSelectedAdmission = () => {
        return (
            <span className={classes.selected}>
                {`${getAdmissionType(selected.admission)} ${moment(selected.admission.startDate).format(dateFormat)}`}
            </span>
        );
    };

    return (
        <>
            <div
                className={clsx(classes.container, classes.divRow)}
                style={{justifyContent: 'space-between', width: '100%'}}
                onClick={handleClick}
            >
                {!selected.admission &&
                    <span className={classes.placeholder}>
                        {t('Choose Spell to navigate to')}
                    </span>
                }

                {selected.admission && renderSelectedAdmission()}

                <div
                    className={classes.divRow}
                    style={{justifyContent: 'end', gap: 5}}
                >
                    {selected.admission &&
                        <IconButton
                            size="small"
                            className={clsx('disabled-options', classes.iconButton, 'clear')}
                            onClick={handleClear}
                        >
                            <ClearIcon fontSize="small"/>
                        </IconButton>
                    }

                    <IconButton
                        size="small"
                        className={clsx('disabled-options', classes.iconButton)}
                        disabled={!selected.index || selected.index === 0}
                        onClick={handleSelectPrev}
                    >
                        <KeyboardArrowUpIcon fontSize="small"/>
                    </IconButton>

                    <IconButton
                        size="small"
                        className={clsx('disabled-options', classes.iconButton)}
                        disabled={selected.index === admissions?.length - 1}
                        onClick={handleSelectNext}
                    >
                        <KeyboardArrowDownIcon fontSize="small"/>
                    </IconButton>
                </div>
            </div>

            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleCloseMenu}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                classes={{
                    paper: 'scroll-normal-white headerPopover',
                }}
            >
                <MenuList>
                    {filteredAdmission?.map((adm, index) => {
                        let color;
                        switch (adm?.attendanceType) {
                            case 'E':
                                color = theme.palette.attendanceType.ed;
                                break;
                            case 'I':
                                color = theme.palette.attendanceType.ip;
                                break;
                            case 'O':
                                color = theme.palette.attendanceType.op;
                                break;
                            default:
                                color = '#222222';
                                break;
                        }

                        return (
                            <MenuItem
                                key={adm?.__id}
                                onClick={() => selectAdmission(adm, index)}
                            >
                                <ListItemIcon>
                                    <div className={classes.attendanceType} style={{backgroundColor: color}}/>
                                </ListItemIcon>

                                <ListItemText>
                                <span className={classes.selected}>
                                    {`${getAdmissionType(adm)} ${moment(adm?.startDate).format(dateFormat)}`}
                                </span>
                                </ListItemText>
                            </MenuItem>
                        );
                    })}
                </MenuList>
            </Popover>
        </>
    );
};

export default ActivitySpellSelector;